// Meta's documented template limits, mirrored from wasapi/app/services/wasapi/templates/validator.rb.
// The UI blocks before the request leaves the browser so a client never accumulates rejections
// against their WhatsApp Business Account — rejections are what put a partner account at risk.
// https://developers.facebook.com/docs/whatsapp/business-management-api/message-templates
export const LIMITS = {
  nameMax: 512,
  headerTextMax: 60,
  headerVariables: 1,
  bodyMax: 1024,
  footerMax: 60,
  buttonsMax: 10,
  buttonTextMax: 25,
  urlButtonsMax: 2,
  urlMax: 2000,
  phoneButtonsMax: 1,
  copyCodeButtonsMax: 1,
  hourlyCreations: 100,
};

export const CATEGORIES = ['MARKETING', 'UTILITY', 'AUTHENTICATION'];

export const HEADER_FORMATS = ['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'DOCUMENT'];

export const BUTTON_TYPES = ['QUICK_REPLY', 'URL', 'PHONE_NUMBER', 'COPY_CODE'];

// Meta only accepts templates edited from these statuses.
export const EDITABLE_STATUSES = ['APPROVED', 'REJECTED', 'PAUSED'];

// Authentication templates are a different shape entirely: Meta writes the body text itself and
// only accepts the security recommendation flag, the code expiration and a single OTP button.
// Sending a BODY `text` is rejected with "componente do tipo BODY contém campo(s) inesperado(s)".
export const AUTH_DEFAULTS = {
  securityRecommendation: true,
  codeExpirationMinutes: 10,
  otpType: 'COPY_CODE',
  otpButtonText: 'Copiar código',
};

export const OTP_TYPES = ['COPY_CODE', 'ONE_TAP'];
export const CODE_EXPIRATION_RANGE = { min: 1, max: 90 };
export const isAuthentication = form => form.category === 'AUTHENTICATION';

export const MEDIA_ACCEPT = {
  IMAGE: 'image/jpeg,image/png',
  VIDEO: 'video/mp4',
  DOCUMENT: 'application/pdf',
};

export const LANGUAGES = [
  'pt_BR',
  'pt_PT',
  'en',
  'en_US',
  'en_GB',
  'es',
  'es_AR',
  'es_ES',
  'es_MX',
  'fr',
  'de',
  'it',
  'nl',
  'ar',
  'hi',
  'id',
  'ja',
  'ko',
  'ru',
  'tr',
  'th',
  'vi',
  'pl',
  'sv',
  'da',
  'nb',
  'fi',
  'cs',
  'el',
  'he',
  'ro',
  'hu',
  'uk',
  'zh_CN',
  'zh_TW',
];

const VARIABLE = /\{\{\s*([a-z0-9_]+)\s*\}\}/gi;

export const variablesIn = (text = '') =>
  [...String(text).matchAll(VARIABLE)].map(match => match[1]);

// Meta only accepts lowercase letters, digits and underscores in a template name.
export const slugifyName = (value = '') =>
  value
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, LIMITS.nameMax);

export const countType = (buttons, type) =>
  (buttons || []).filter(button => button.type === type).length;

// Mirrors the server-side validator. Returns an array of i18n keys under WASAPI_TEMPLATES.ERRORS.
export const validateTemplate = form => {
  const errors = [];
  const push = (key, params = {}) => errors.push({ key, params });

  if (!form.name) push('NAME_BLANK');
  else if (!/^[a-z0-9_]+$/.test(form.name)) push('NAME_FORMAT');

  if (!CATEGORIES.includes(form.category)) push('CATEGORY');
  if (!form.language) push('LANGUAGE');

  if (isAuthentication(form)) {
    if (!form.otpButtonText.trim()) push('OTP_TEXT_BLANK');
    if (form.otpButtonText.length > LIMITS.buttonTextMax)
      push('BUTTON_TEXT_TOO_LONG', { max: LIMITS.buttonTextMax });
    const minutes = Number(form.codeExpirationMinutes);
    if (
      !Number.isInteger(minutes) ||
      minutes < CODE_EXPIRATION_RANGE.min ||
      minutes > CODE_EXPIRATION_RANGE.max
    )
      push('CODE_EXPIRATION', CODE_EXPIRATION_RANGE);
    return errors;
  }

  const bodyVars = variablesIn(form.body);
  if (!form.body.trim()) push('BODY_BLANK');
  if (form.body.length > LIMITS.bodyMax)
    push('BODY_TOO_LONG', { max: LIMITS.bodyMax });
  if (bodyVars.some((_, index) => !form.bodyExamples[index]?.trim()))
    push('BODY_EXAMPLES');

  if (form.headerFormat === 'TEXT') {
    const headerVars = variablesIn(form.headerText);
    if (!form.headerText.trim()) push('HEADER_BLANK');
    if (form.headerText.length > LIMITS.headerTextMax)
      push('HEADER_TOO_LONG', { max: LIMITS.headerTextMax });
    if (headerVars.length > LIMITS.headerVariables) push('HEADER_ONE_VARIABLE');
    if (headerVars.length && !form.headerExample.trim())
      push('HEADER_EXAMPLES');
  } else if (form.headerFormat !== 'NONE' && !form.headerHandle) {
    push('HEADER_HANDLE');
  }

  if (form.footer.length > LIMITS.footerMax)
    push('FOOTER_TOO_LONG', { max: LIMITS.footerMax });
  if (variablesIn(form.footer).length) push('FOOTER_VARIABLES');

  if (form.buttons.length > LIMITS.buttonsMax)
    push('BUTTONS_MAX', { max: LIMITS.buttonsMax });
  if (countType(form.buttons, 'URL') > LIMITS.urlButtonsMax)
    push('URL_BUTTONS_MAX', { max: LIMITS.urlButtonsMax });
  if (countType(form.buttons, 'PHONE_NUMBER') > LIMITS.phoneButtonsMax)
    push('PHONE_NUMBER_BUTTONS_MAX', { max: LIMITS.phoneButtonsMax });
  if (countType(form.buttons, 'COPY_CODE') > LIMITS.copyCodeButtonsMax)
    push('COPY_CODE_BUTTONS_MAX', { max: LIMITS.copyCodeButtonsMax });

  form.buttons.forEach(button => {
    if (button.type !== 'COPY_CODE' && !button.text.trim())
      push('BUTTON_TEXT_BLANK');
    if (button.text.length > LIMITS.buttonTextMax)
      push('BUTTON_TEXT_TOO_LONG', { max: LIMITS.buttonTextMax });
    if (button.type === 'URL') {
      if (!button.url.trim()) push('BUTTON_URL_BLANK');
      if (variablesIn(button.url).length > 1) push('BUTTON_URL_VARIABLE');
    }
    if (button.type === 'PHONE_NUMBER' && !button.phoneNumber.trim())
      push('BUTTON_PHONE_BLANK');
  });

  return errors;
};

// Builds the exact payload shape Meta expects from the flat form the UI edits.
export const buildComponents = form => {
  const components = [];

  if (isAuthentication(form)) {
    return [
      {
        type: 'BODY',
        add_security_recommendation: form.securityRecommendation,
      },
      {
        type: 'FOOTER',
        code_expiration_minutes: Number(form.codeExpirationMinutes),
      },
      {
        type: 'BUTTONS',
        buttons: [
          { type: 'OTP', otp_type: form.otpType, text: form.otpButtonText },
        ],
      },
    ];
  }

  if (form.headerFormat === 'TEXT') {
    const header = { type: 'HEADER', format: 'TEXT', text: form.headerText };
    if (variablesIn(form.headerText).length)
      header.example = { header_text: [form.headerExample] };
    components.push(header);
  } else if (form.headerFormat !== 'NONE') {
    components.push({
      type: 'HEADER',
      format: form.headerFormat,
      example: { header_handle: [form.headerHandle] },
    });
  }

  const body = { type: 'BODY', text: form.body };
  const bodyVars = variablesIn(form.body);
  if (bodyVars.length)
    body.example = {
      body_text: [bodyVars.map((_, i) => form.bodyExamples[i])],
    };
  components.push(body);

  if (form.footer.trim())
    components.push({ type: 'FOOTER', text: form.footer });

  if (form.buttons.length) {
    components.push({
      type: 'BUTTONS',
      buttons: form.buttons.map(button => {
        if (button.type === 'URL')
          return { type: 'URL', text: button.text, url: button.url };
        if (button.type === 'PHONE_NUMBER')
          return {
            type: 'PHONE_NUMBER',
            text: button.text,
            phone_number: button.phoneNumber,
          };
        if (button.type === 'COPY_CODE')
          return { type: 'COPY_CODE', example: button.example };
        return { type: 'QUICK_REPLY', text: button.text };
      }),
    });
  }

  return components;
};

// Inverse of buildComponents, so an existing Meta template can be reopened in the editor.
export const formFromTemplate = template => {
  const components = template.components || [];
  const find = type => components.find(c => c.type === type);
  const header = find('HEADER') || {};
  const body = find('BODY') || {};
  const footer = find('FOOTER') || {};
  const buttons = find('BUTTONS')?.buttons || [];

  const otp = buttons.find(button => button.type === 'OTP') || {};

  return {
    securityRecommendation: body.add_security_recommendation !== false,
    codeExpirationMinutes:
      footer.code_expiration_minutes || AUTH_DEFAULTS.codeExpirationMinutes,
    otpType: otp.otp_type || AUTH_DEFAULTS.otpType,
    otpButtonText: otp.text || AUTH_DEFAULTS.otpButtonText,
    name: template.name || '',
    category: template.category || 'UTILITY',
    language: template.language || 'pt_BR',
    headerFormat: header.format || 'NONE',
    headerText: header.text || '',
    headerExample: header.example?.header_text?.[0] || '',
    headerHandle: header.example?.header_handle?.[0] || '',
    headerFileName: '',
    body: body.text || '',
    bodyExamples: body.example?.body_text?.[0] || [],
    footer: footer.text || '',
    buttons: buttons.map(button => ({
      type: button.type,
      text: button.text || '',
      url: button.url || '',
      phoneNumber: button.phone_number || '',
      example: Array.isArray(button.example)
        ? button.example[0]
        : button.example || '',
    })),
  };
};

export const emptyForm = () => ({
  ...AUTH_DEFAULTS,
  name: '',
  category: 'UTILITY',
  language: 'pt_BR',
  headerFormat: 'NONE',
  headerText: '',
  headerExample: '',
  headerHandle: '',
  headerFileName: '',
  body: '',
  bodyExamples: [],
  footer: '',
  buttons: [],
});

export const STATUS_TONE = {
  APPROVED: 'bg-n-teal-3 text-n-teal-11',
  PENDING: 'bg-n-amber-3 text-n-amber-11',
  IN_APPEAL: 'bg-n-amber-3 text-n-amber-11',
  REJECTED: 'bg-n-ruby-3 text-n-ruby-11',
  PAUSED: 'bg-n-ruby-3 text-n-ruby-11',
  DISABLED: 'bg-n-ruby-3 text-n-ruby-11',
};
